// src/components/SpearDriveWebsite.tsx
import React, { useState } from 'react';
import { Shield, Users, Mail, Phone, MapPin, ChevronRight, Star, Lock, Zap, Settings } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  company: string;
  inquiryType: 'investment' | 'partnership' | 'purchase' | 'other';
  message: string;
}

const SpearDriveWebsite: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<string>('home');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    inquiryType: 'investment',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = () => {
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      alert('Please fill in all required fields.');
      return;
    }

    alert('Thank you for your inquiry! We will get back to you soon.');
    setFormData({
      name: '',
      email: '',
      company: '',
      inquiryType: 'investment',
      message: ''
    });
  };

  const Navigation: React.FC = () => (
    <nav className="bg-gray-900 text-white p-4 sticky top-0 z-50 backdrop-blur-sm bg-opacity-95">
      <div className="max-w-6xl mx-auto flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Shield className="h-8 w-8 text-blue-400" />
          <span className="text-xl font-bold">SpearDrive</span>
        </div>
        <div className="flex space-x-6">
          <button 
            onClick={() => setCurrentPage('home')}
            className={`hover:text-blue-400 transition-colors ${currentPage === 'home' ? 'text-blue-400' : ''}`}
          >
            Home
          </button>
          <button 
            onClick={() => setCurrentPage('about')}
            className={`hover:text-blue-400 transition-colors ${currentPage === 'about' ? 'text-blue-400' : ''}`}
          >
            About
          </button>
          <button 
            onClick={() => setCurrentPage('contact')}
            className={`hover:text-blue-400 transition-colors ${currentPage === 'contact' ? 'text-blue-400' : ''}`}
          >
            Contact
          </button>
        </div>
      </div>
    </nav>
  );

  const HomePage: React.FC = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-800">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
        <div className="relative max-w-6xl mx-auto px-4 py-24 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
            Next-Generation Armored Vehicles
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl mx-auto">
            We bring modern technology to these platforms to move the industry forward. Our vehicles are capable of performing at the upper levels of civilian and NGO operations. 
          </p>
          <button 
            onClick={() => setCurrentPage('contact')}
            className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Get Started <ChevronRight className="inline ml-2 h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-gray-800">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            Revolutionary Features
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-700 to-gray-800 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Lock className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Advanced Armor Technology</h3>
              <p className="text-gray-300">
                Ceramic glass reduces thickness by 37% while maintaining B8 ballistic protection.
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-700 to-gray-800 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="bg-purple-600 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Smart Defense Systems</h3>
              <p className="text-gray-300">
                AI-powered threat detection and automated countermeasure deployment for real-time protection in dynamic environments.
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-700 to-gray-800 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="bg-cyan-600 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Settings className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Operate Anywhere</h3>
              <p className="text-gray-300">
                Operate the vehicle from any seat to evacuate danger.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Technology Section */}
      <div className="py-20 bg-gradient-to-br from-blue-900 to-purple-900">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-8">Cutting-Edge Integration</h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Our vehicles incorporate modern, proven technologies in materials science, electronics, and automotive engineering to deliver unparalleled, fully integrated performance.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Star className="h-6 w-6" />, title: "Ballistic Protection", desc: "Level B8, 5 consecutive shots from a 7.62 x 51 NATO round from all exterior surfaces. (And the windows still roll down.)" },
              { icon: <Shield className="h-6 w-6" />, title: "Interior Safety", desc: "Every seat offers B8 protection and is stocked with a SBR and GO-bag in case of exfiltration." },
              { icon: <Zap className="h-6 w-6" />, title: "Electronic Integration", desc: "360 Visual and Thermal Cameras for situational awareness, integrated with modern AI systems." },
              { icon: <Settings className="h-6 w-6" />, title: "Operational Continuity", desc: "Driving and defensive systems available from every seat in case driver or any Operator is incapacitated." }
            ].map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <div className="text-blue-400 mb-3">{item.icon}</div>
                <h3 className="font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-300 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const AboutPage: React.FC = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 py-12">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-6">About SpearDrive</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Founded by industry veterans with decades of experience in defense and automotive engineering.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-gray-800 p-8 rounded-2xl shadow-xl">
            <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
            <p className="text-gray-300 mb-4">
              To revolutionize personal and commercial security transportation through innovative armored vehicle designs that prioritize safety, comfort, and reliability.
            </p>
            <p className="text-gray-300">
              We believe that protection shouldn't come at the expense of comfort, performance, or technological advancement. Our vehicles represent the perfect fusion of security and sophistication.
            </p>
          </div>
          <div className="bg-gray-800 p-8 rounded-2xl shadow-xl">
            <h2 className="text-3xl font-bold text-white mb-6">Our Vision</h2>
            <p className="text-gray-300 mb-4">
              To become the global leader in next-generation armored vehicle technology, setting new standards for comfort, protection, innovation, and customer satisfaction.
            </p>
            <p className="text-gray-300">
              We envision a future where advanced protection technology is accessible, reliable, and seamlessly integrated into everyday transportation needs.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-800 to-purple-800 p-8 rounded-2xl shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Leadership Team</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="text-center">
              <div className="bg-gray-700 w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Users className="h-12 w-12 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Joe Goacher</h3>
              <p className="text-blue-300 mb-2">CEO & Co-Founder</p>
              <p className="text-gray-300 text-sm">Joe’s businesses have built, imported, and designed vehicles for demanding customers, spanning decades. His successful automotive businesses and defense experience make him uniquely positioned to drive innovation in the Armored vehicle space.</p>
            </div>
            <div className="text-center">
              <div className="bg-gray-700 w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Users className="h-12 w-12 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Eric Psalmond</h3>
              <p className="text-purple-300 mb-2">Head of Electrical and Software Engineering</p>
              <p className="text-gray-300 text-sm">Eric, an expert in software and with experience in consumer products, has enhanced safety for millions and revitalized major brands' online presence. His focus on continuous improvement makes him ideal for innovative solutions combining hardware and software.</p>
            </div>
            {/* <div className="text-center">
              <div className="bg-gray-700 w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Users className="h-12 w-12 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">SomeGuy HisLastName</h3>
              <p className="text-cyan-300 mb-2">Head of Stuff</p>
              <p className="text-gray-300 text-sm">Does things too.</p>
            </div> */}
          </div>
        </div>

        <div className="bg-gray-800 p-8 rounded-2xl shadow-xl">
          <h2 className="text-3xl font-bold text-white mb-6">Company Values</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-4">
              <div className="bg-blue-600 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white font-bold text-sm">1</span>
              </div>
              <div>
                <h3 className="text-white font-bold mb-2">Innovation First</h3>
                <p className="text-gray-300">Continuously pushing the boundaries of what's possible in armored vehicle technology.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="bg-purple-600 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white font-bold text-sm">2</span>
              </div>
              <div>
                <h3 className="text-white font-bold mb-2">Customer Focus</h3>
                <p className="text-gray-300">Every design decision is made with our customers' safety and satisfaction in mind.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="bg-cyan-600 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white font-bold text-sm">3</span>
              </div>
              <div>
                <h3 className="text-white font-bold mb-2">Quality Excellence</h3>
                <p className="text-gray-300">Uncompromising standards in manufacturing, testing, and delivery of our vehicles.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="bg-green-600 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white font-bold text-sm">4</span>
              </div>
              <div>
                <h3 className="text-white font-bold mb-2">Ethical Business</h3>
                <p className="text-gray-300">Operating with transparency, integrity, and respect for all stakeholders.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ContactPage: React.FC = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 py-12">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-6">Contact Us</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to discuss investment opportunities, partnerships, or vehicle purchases? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* <div className="bg-gray-800 p-8 rounded-2xl shadow-xl">
            <h2 className="text-3xl font-bold text-white mb-6">Get In Touch</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-white font-medium mb-2">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-blue-400 focus:outline-none"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="block text-white font-medium mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-blue-400 focus:outline-none"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-white font-medium mb-2">Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-blue-400 focus:outline-none"
                  placeholder="Your company name"
                />
              </div>
              <div>
                <label className="block text-white font-medium mb-2">Inquiry Type *</label>
                <select
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleInputChange}
                  className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-blue-400 focus:outline-none"
                >
                  <option value="investment">Investment Opportunity</option>
                  <option value="partnership">Partnership</option>
                  <option value="purchase">Vehicle Purchase</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-white font-medium mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-blue-400 focus:outline-none"
                  placeholder="Talk to us..."
                />
              </div>
              <button
                type="button"
                onClick={handleSubmit}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 px-6 py-3 rounded-lg font-semibold text-white transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Send Message
              </button>
            </div>
          </div> */}

          <div className="space-y-8">
            <div className="bg-gray-800 p-8 rounded-2xl shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-600 w-12 h-12 rounded-full flex items-center justify-center">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Email</p>
                    <p className="text-gray-300"><a href="mailto:contact@speardrive.com">contact@speardrive.com</a></p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <div className="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center">
                    <Phone className="h-6 w-6 text-white" />
                  </div> */}
                  {/* <div>
                    <p className="text-white font-medium">Phone</p>
                    <p className="text-gray-300">+1 (555) 123-ARMOR</p>
                  </div> */}
                </div>
                <div className="flex items-center space-x-4">
                  <div className="bg-cyan-600 w-12 h-12 rounded-full flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Address</p>
                    <p className="text-gray-300">827 S Lincoln Ave<br />Loveland, CO 80537</p>
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="bg-gradient-to-r from-blue-800 to-purple-800 p-8 rounded-2xl shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-4">Business Hours</h3>
              <div className="space-y-2 text-gray-300">
                <p>Monday - Friday: 8:00 AM - 6:00 PM PST</p>
                <p>Saturday: 9:00 AM - 2:00 PM PST</p>
                <p>Sunday: Closed</p>
              </div>
              <p className="text-blue-300 mt-4">Emergency inquiries available 24/7</p>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="font-sans">
      <Navigation />
      {currentPage === 'home' && <HomePage />}
      {currentPage === 'about' && <AboutPage />}
      {currentPage === 'contact' && <ContactPage />}
    </div>
  );
};

export default SpearDriveWebsite;